import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { FormsService } from '@shared/services/forms.service';
import {
  TuiDataListDropdownManagerModule,
  TuiInputModule,
  TuiInputNumberModule,
} from '@taiga-ui/kit';
import {
  TuiDataListModule,
  TuiDialogService,
  TuiDropdownModule,
  TuiGroupModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { MaskitoModule } from '@maskito/angular';
import { phoneMask } from '@shared/utils/masks';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { TuiActiveZoneModule } from '@taiga-ui/cdk';
import {
  catchError,
  delay,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
  combineLatest,
  Observable,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@shared/services/alert.service';
import { fromFormControlValue } from '@shared/utils/observables';
import { AsyncPipe } from '@angular/common';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import {
  ClientFormComponent,
  ClientFormInputData,
} from '@shared/components/catalogs/client-form/client-form.component';
import { Payment, PaymentForm } from '../../../../../../types/payment';
import { CargoCalculationService } from '../../../../services/cargo-calculation.service';
import {
  PAYMENT_METHODS,
  PAYMENT_TYPES,
} from '../../../../../../types/payment-type';
import { StorageKeeper } from '../../../../../../types/client';

@Component({
  selector: 'app-cargo-payment',
  standalone: true,
  imports: [
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiInputNumberModule,
    TuiGroupModule,
    MaskitoModule,
    CheckboxComponent,
    AutocompleteComponent,
    ReactiveFormsModule,
    TuiActiveZoneModule,
    AsyncPipe,
    InputDateComponent,
    TuiDropdownModule,
    TuiDataListModule,
    TuiDataListDropdownManagerModule,
  ],
  templateUrl: './cargo-payment.component.html',
  styleUrl: './cargo-payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CargoPaymentComponent implements OnInit {
  update$ = new Subject<void>();
  form = this.fb.group<PaymentForm>({
    calculatedRateJson: this.forms.getCalculatedJsonForm(),
    id: this.fb.control(null),
    divisionId: this.fb.control(null),
    cargoId: this.fb.control(null),
    clientJson: this.forms.getClientJsonForm(),
    dealDetailsJson: this.forms.getDealDetailsJsonForm(),
  });

  openEditClientDialog$ = new Subject<void>();

  currencyLetter$ = fromFormControlValue(
    this.form.controls.calculatedRateJson.controls.currencyLetter,
  );

  clientContacts$: Observable<Array<StorageKeeper & { search: string }>> =
    combineLatest([
      fromFormControlValue(this.form.controls.clientJson.controls.id),
      this.cargoCatalogsService.clients$,
    ]).pipe(
      map(([clientId, clients]) => {
        return (
          clients.find(c => c.id === clientId)?.storageKeepersJson ?? []
        ).map(contact => {
          return {
            ...contact,
            search: `${contact.name} ${contact.phone}`,
          };
        });
      }),
    );

  protected readonly phoneMask = phoneMask;
  protected readonly PAYMENT_TYPES = PAYMENT_TYPES;
  protected readonly PAYMENT_METHODS = PAYMENT_METHODS;
  constructor(
    private readonly fb: FormBuilder,
    private readonly forms: FormsService,
    private readonly alert: AlertService,
    private readonly cargoCalculationService: CargoCalculationService,
    private readonly destroyRef: DestroyRef,
    private readonly cargoCatalogsService: CargoCatalogsService,
    private readonly dialogs: TuiDialogService,
  ) {}

  onZoneChange(focused: boolean) {
    if (!focused) {
      this.update$.next();
    }
  }

  onUpdate() {
    this.update$
      .pipe(
        delay(0),
        filter(() => !this.form.pristine),
        switchMap(() =>
          this.cargoCalculationService.savePayment(this.form.getRawValue()),
        ),
        tap(() => {
          this.alert.showSuccess();
          this.form.markAsUntouched();
          this.form.markAsPristine();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onEditClientClick() {
    this.openEditClientDialog$
      .pipe(
        switchMap(() => {
          const client = this.getClientDetailsFromCatalog();
          const data: ClientFormInputData = {
            client,
          };
          return this.dialogs.open(
            new PolymorpheusComponent(ClientFormComponent),
            {
              size: 'auto',
              data,
            },
          );
        }),
        tap(() => this.cargoCatalogsService.fetchClients$.next()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  getClientDetailsFromCatalog() {
    const clientId = this.form.controls.clientJson.controls.id.getRawValue();
    const clients = this.cargoCatalogsService.clientMap;
    return clients.get(clientId?.toString() ?? '');
  }

  fillNameOnPhoneSelect(phone: Nullable<string>): void {
    const current = this.getClientDetailsFromCatalog();
    const recipientName = current?.storageKeepersJson.find(
      k => k.phone === phone,
    )?.name;
    this.form.controls.dealDetailsJson.controls.recipientName.setValue(
      recipientName ?? null,
    );
  }

  @Input() set payment(value: Payment) {
    this.form.setValue(value);
  }

  disableFields() {
    this.form.controls.clientJson.disable();
    this.form.controls.calculatedRateJson.controls.calculatedSum.disable();
  }

  ngOnInit() {
    this.onUpdate();
    this.disableFields();
    this.onEditClientClick();
  }
}
